/**
 * Paste Import Modal Component
 * Modal for pasting cutting machine data from spreadsheets.
 */
import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ClipboardPaste, Calendar, AlertTriangle, Loader2 } from 'lucide-react';

export function PasteImportModal({
  open,
  onOpenChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  text,
  onTextChange,
  onPaste,
  loading,
  detectedDays,
  parsedLines,
  existingDates = [],
  showReplaceWarning = false,
  onReplaceConfirm,
  onReplaceCancel
}) {
  // Count lines when text changes
  useEffect(() => {
    if (text) {
      const lines = text.split('\n').filter(l => l.trim()).length;
      // This would be handled by parent
    }
  }, [text]);

  const hasExistingDates = existingDates.length > 0;

  return (
    <>
      {/* Main Paste Modal */}
      <Dialog open={open && !showReplaceWarning} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" />
              Importar Dados (Colar)
            </DialogTitle>
            <DialogDescription>
              Cole os dados copiados do Excel ou software da máquina de corte.
              O sistema irá processar e calcular automaticamente as métricas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paste-start-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Data Início
                </Label>
                <Input
                  id="paste-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paste-end-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Data Fim (opcional)
                </Label>
                <Input
                  id="paste-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  min={startDate}
                />
                <p className="text-xs text-muted-foreground">
                  Deixe vazio para importar apenas um dia
                </p>
              </div>
            </div>

            {/* Text Area */}
            <div className="space-y-2">
              <Label htmlFor="paste-text">Dados (Cole aqui)</Label>
              <Textarea
                id="paste-text"
                placeholder="Cole aqui os dados copiados do Excel...&#10;&#10;Formato esperado:&#10;Plano | Peças | Perímetro | Tempo Corte | Execução | Hora Início | Hora Fim | Parado | Entre Marcadas | V.Real | V.Média"
                value={text}
                onChange={(e) => onTextChange(e.target.value)}
                className="min-h-[200px] font-mono text-sm"
              />
            </div>

            {/* Detection Info */}
            {text && (
              <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                <Badge variant="outline">
                  {text.split('\n').filter(l => l.trim()).length} linhas
                </Badge>
                {detectedDays > 0 && (
                  <Badge variant="secondary">
                    {detectedDays} dia(s) detectado(s)
                  </Badge>
                )}
                {parsedLines > 0 && (
                  <Badge variant="secondary">
                    {parsedLines} operações válidas
                  </Badge>
                )}
              </div>
            )}

            {/* Warning for existing dates */}
            {hasExistingDates && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 text-yellow-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Datas já existentes</p>
                  <p className="text-sm">
                    Os seguintes dias já têm dados: {existingDates.join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              onClick={() => onPaste(false)} 
              disabled={loading || !startDate || !text.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A processar...
                </>
              ) : (
                'Importar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Confirmation Modal */}
      <Dialog open={showReplaceWarning} onOpenChange={() => onReplaceCancel?.()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Dados Existentes
            </DialogTitle>
            <DialogDescription>
              Já existem dados para as datas selecionadas. O que deseja fazer?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">Datas com dados existentes:</p>
            <div className="flex flex-wrap gap-2">
              {existingDates.map(date => (
                <Badge key={date} variant="secondary">{date}</Badge>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onReplaceCancel}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={onReplaceConfirm}>
              Substituir Dados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PasteImportModal;
